/**
 * PeerConnection - WebRTC Peer Connection Wrapper
 * * Wraps PeerJS library to provide a clean interface for
 * peer-to-peer connections in the chess game.
 */

import Peer, { DataConnection, MediaConnection, PeerOptions } from 'peerjs';
import { ConnectionStatus, ConnectionState } from '../types';
import { 
    GameMessage, 
    deserializeMessage, 
    serializeMessage,
    createPingMessage,
    createPongMessage,
    MessageType
} from './messages';

// ============================================
// Configuration
// ============================================

/**
 * PeerJS configuration options
 */
export type PeerConfig = PeerOptions;

/**
 * PeerJS server configurations with fallbacks
 * Using reliable public PeerJS servers
 */
const PEER_SERVERS: PeerConfig[] = [
    // Primary: Default PeerJS Cloud (auto-selects best server)
    {
        debug: 1,
    },
    // Fallback 1: Explicit PeerJS Cloud server 0
    {
        debug: 1,
        host: '0.peerjs.com',
        port: 443,
        secure: true,
        path: '/',
        key: 'peerjs',
    },
    // Fallback 2: Scaledrone free TURN/STUN with PeerJS
    {
        debug: 1,
        config: {
            // Added Google STUN servers for robust ICE candidate gathering
            iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
                { urls: 'stun:stun2.l.google.com:19302' },
                { urls: 'stun:stun3.l.google.com:19302' },
                { urls: 'stun:stun4.l.google.com:19302' },
            ],
        },
    },
];

/**
 * Default PeerJS configuration (uses free PeerJS cloud server)
 */
const DEFAULT_PEER_CONFIG: PeerConfig = PEER_SERVERS[0];

/**
 * Connection timeouts and intervals
 */
const CONNECTION_TIMEOUT = 90000; // 90 seconds total timeout
const PER_SERVER_TIMEOUT = 20000; // 20 seconds per server attempt
const DATA_CONNECTION_TIMEOUT = 15000; // 15 seconds for data connection after peer is open
const HEARTBEAT_INTERVAL = 5000; // 5 seconds
const HEARTBEAT_TIMEOUT = 30000; // Increased from 15s to 30s for unstable connections
const RECONNECT_BASE_DELAY = 1000; // 1 second
const RECONNECT_MAX_DELAY = 30000; // 30 seconds
const MAX_RECONNECT_ATTEMPTS = 5;

// ============================================
// PeerConnection Class
// ============================================

/**
 * PeerConnection class wrapping PeerJS for WebRTC communication
 */
export class PeerConnection {
    private peer: Peer | null = null;
    private connection: DataConnection | null = null;
    private roomId: string = '';
    private isHost: boolean = false;
    private peerId: string = '';

    private mediaConnection: MediaConnection | null = null;
    private currentLocalStream: MediaStream | null = null;
    
    // Server fallback management
    private currentServerIndex: number = 0;
    
    // Heartbeat management
    private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
    private lastHeartbeat: number = 0;
    private pingSequence: number = 0;
    private pendingPings: Map<number, number> = new Map();
    private latency: number = 0;
    
    // Reconnection management
    private reconnectAttempts: number = 0;
    private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    private shouldReconnect: boolean = false;
    private lastKnownPeerId: string = '';
    
    // Connection state
    private connectionState: ConnectionState = {
        status: ConnectionStatus.DISCONNECTED,
        peerId: null,
        roomId: null,
        opponentId: null,
        opponentName: null,
        latency: 0,
        lastHeartbeat: 0,
    };
    
    // Event callbacks
    public onConnected: ((peerId: string) => void) | null = null;
    public onDisconnected: ((reason: string) => void) | null = null;
    public onMessage: ((message: GameMessage) => void) | null = null;
    public onError: ((error: Error) => void) | null = null;
    public onStateChange: ((state: ConnectionState) => void) | null = null;
    public onLatencyUpdate: ((latency: number) => void) | null = null;
    // NOTE: Changed onIncomingCall signature to match VideoCall component
    public onIncomingCall: ((call: MediaConnection) => void) | null = null; 
    public onCallEnded: (() => void) | null = null;
    
    /**
     * Create a new PeerConnection instance
     */
    constructor(private config: PeerConfig = DEFAULT_PEER_CONFIG) {}
    
    // ============================================
    // Public Methods
    // ============================================
    
    /**
     * Create a new room as host
     * Returns the room ID (which is the peer ID)
     * Tries multiple servers with fallback
     */
    async createRoom(): Promise<string> {
        this.isHost = true;
        this.updateState({ status: ConnectionStatus.CONNECTING });
        
        // Try each server in order
        for (let i = 0; i < PEER_SERVERS.length; i++) {
            this.currentServerIndex = i;
            const serverConfig = PEER_SERVERS[i];
            
            try {
                console.log(`Trying PeerJS server ${i + 1}/${PEER_SERVERS.length}:`, serverConfig.host || 'default');
                const roomId = await this.tryCreateRoom(serverConfig);
                return roomId;
            } catch (error) {
                console.warn(`Server ${i + 1} failed:`, error);
                // Clean up before trying next server
                this.cleanupPeer();
                
                // If this was the last server, throw the error
                if (i === PEER_SERVERS.length - 1) {
                    this.updateState({ status: ConnectionStatus.ERROR });
                    throw new Error('Impossible de se connecter au serveur. Veuillez réessayer.');
                }
            }
        }
        
        throw new Error('Impossible de créer la salle');
    }
    
    /**
     * Try to create a room with a specific server config
     */
    private tryCreateRoom(serverConfig: PeerConfig): Promise<string> {
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                reject(new Error('Délai de connexion dépassé'));
                this.cleanupPeer();
            }, PER_SERVER_TIMEOUT); // Fixed timeout per server
            
            try {
                // Create peer with random ID
                this.peer = new Peer(serverConfig);
                
                this.peer.on('open', (id: string) => {
                    clearTimeout(timeoutId);
                    this.peerId = id;
                    this.roomId = id; // Room ID is the host's peer ID
                    
                    this.updateState({
                        status: ConnectionStatus.CONNECTED,
                        peerId: id,
                        roomId: id,
                    });
                    
                    this.setupHostListeners();
                    this.setupMediaCallListener(); // <<< ADDED MEDIA LISTENER
                    resolve(id);
                });
                
                this.peer.on('error', (err: Error) => {
                    clearTimeout(timeoutId);
                    reject(err);
                });
                
            } catch (error) {
                clearTimeout(timeoutId);
                reject(error);
            }
        });
    }
    
    /**
     * Clean up peer without full cleanup
     */
    private cleanupPeer(): void {
        if (this.peer) {
            try {
                this.peer.destroy();
            } catch (e) {
                // Ignore errors during cleanup
            }
            this.peer = null;
        }
    }
    
    /**
     * Join an existing room as guest
     * Tries multiple servers with fallback
     */
    async joinRoom(roomId: string): Promise<void> {
        this.isHost = false;
        this.roomId = roomId;
        this.lastKnownPeerId = roomId;
        this.updateState({ status: ConnectionStatus.CONNECTING, roomId });
        
        // Try each server in order
        for (let i = 0; i < PEER_SERVERS.length; i++) {
            this.currentServerIndex = i;
            const serverConfig = PEER_SERVERS[i];
            
            try {
                console.log(`Trying PeerJS server ${i + 1}/${PEER_SERVERS.length}:`, serverConfig.host || 'default');
                await this.tryJoinRoom(roomId, serverConfig);
                return;
            } catch (error) {
                console.warn(`Server ${i + 1} failed:`, error);
                // Clean up before trying next server
                this.cleanupPeer();
                
                // If this was the last server, throw the error
                if (i === PEER_SERVERS.length - 1) {
                    this.updateState({ status: ConnectionStatus.ERROR });
                    throw new Error('Impossible de rejoindre la salle. Vérifiez le code.');
                }
            }
        }
        
        throw new Error('Impossible de rejoindre la salle');
    }
    
    /**
     * Try to join a room with a specific server config
     */
    private tryJoinRoom(roomId: string, serverConfig: PeerConfig): Promise<void> {
        return new Promise((resolve, reject) => {
            let peerOpenTimeout: ReturnType<typeof setTimeout> | null = null;
            let dataConnectionTimeout: ReturnType<typeof setTimeout> | null = null;
            let resolved = false;
            
            const cleanup = () => {
                if (peerOpenTimeout) clearTimeout(peerOpenTimeout);
                if (dataConnectionTimeout) clearTimeout(dataConnectionTimeout);
            };
            
            const rejectOnce = (error: Error) => {
                if (resolved) return;
                resolved = true;
                cleanup();
                reject(error);
            };
            
            const resolveOnce = () => {
                if (resolved) return;
                resolved = true;
                cleanup();
                resolve();
            };
            
            // Timeout for peer to open (connect to signaling server)
            peerOpenTimeout = setTimeout(() => {
                rejectOnce(new Error('Peer connection timeout'));
                this.cleanupPeer();
            }, PER_SERVER_TIMEOUT);
            
            try {
                // Create peer with random ID
                this.peer = new Peer(serverConfig);
                
                this.peer.on('open', (id: string) => {
                    if (peerOpenTimeout) clearTimeout(peerOpenTimeout);
                    
                    console.log(`Peer opened with ID: ${id}, connecting to host: ${roomId}`);
                    this.peerId = id;
                    this.updateState({ peerId: id });
                    
                    this.setupMediaCallListener(); // <<< ADDED MEDIA LISTENER
                    
                    // Set timeout for data connection
                    dataConnectionTimeout = setTimeout(() => {
                        console.warn('Data connection timeout - host may not be available');
                        rejectOnce(new Error('Impossible de se connecter à l\'hôte. La salle n\'existe peut-être plus.'));
                        this.cleanupPeer();
                    }, DATA_CONNECTION_TIMEOUT);
                    
                    // Connect to the host
                    this.connection = this.peer!.connect(roomId, {
                        reliable: true,
                    });
                    
                    // Handle connection errors
                    this.connection.on('error', (err: Error) => {
                        console.error('Data connection error:', err);
                        rejectOnce(new Error('Erreur de connexion: ' + err.message));
                    });
                    
                    this.setupConnectionListeners(this.connection, () => {
                        console.log('Data connection established successfully');
                        this.updateState({
                            status: ConnectionStatus.CONNECTED,
                            opponentId: roomId,
                        });
                        this.startHeartbeat();
                        resolveOnce();
                    });
                });
                
                this.peer.on('error', (err: Error) => {
                    console.error('Peer error:', err);
                    rejectOnce(err);
                });
                
                this.peer.on('disconnected', () => {
                    console.warn('Peer disconnected from signaling server');
                    if (!resolved) {
                        rejectOnce(new Error('Déconnecté du serveur de signalisation'));
                    }
                });
                
            } catch (error) {
                rejectOnce(error instanceof Error ? error : new Error(String(error)));
            }
        });
    }
    
    /**
     * Send a message to the connected peer
     */
    send(message: GameMessage): void {
        if (!this.connection || !this.isConnected()) {
            console.warn('Cannot send message: not connected');
            return;
        }
        
        try {
            const serialized = serializeMessage(message);
            this.connection.send(serialized);
        } catch (error) {
            console.error('Error sending message:', error);
            this.handleError(error instanceof Error ? error : new Error(String(error)));
        }
    }
    
    /**
     * Disconnect from the current connection
     */
    disconnect(): void {
        this.shouldReconnect = false;
        this.cleanup();
        this.updateState({
            status: ConnectionStatus.DISCONNECTED,
            opponentId: null,
            opponentName: null,
        });
        this.onDisconnected?.('User disconnected');
    }
    
    /**
     * Get the current connection state
     */
    getConnectionState(): ConnectionState {
        return { ...this.connectionState };
    }
    
    /**
     * Check if currently connected to a peer
     */
    isConnected(): boolean {
        return (
            this.connectionState.status === ConnectionStatus.CONNECTED &&
            this.connection !== null &&
            this.connection.open
        );
    }
    
    /**
     * Get the current room ID
     */
    getRoomId(): string {
        return this.roomId;
    }
    
    /**
     * Get the local peer ID
     */
    getPeerId(): string {
        return this.peerId;
    }
    
    /**
     * Check if this peer is the host
     */
    getIsHost(): boolean {
        return this.isHost;
    }
    
    /**
     * Get current latency in milliseconds
     */
    getLatency(): number {
        return this.latency;
    }
    
    
    /**
     * Initiate a media call (Audio/Video) to the connected peer.
     * @param stream The local MediaStream (from getUserMedia) to send.
     */
    callPeer(stream: MediaStream): MediaConnection {
        if (!this.peer || !this.connectionState.opponentId) {
            throw new Error('Cannot start call: Peer or opponent not connected.');
        }

        if (this.mediaConnection) {
            console.warn('Call already in progress, destroying previous one.');
            this.endCall();
        }
        
        this.currentLocalStream = stream;
        
        // PeerJS 'call' handles the WebRTC Offer/Answer/ICE
        const call = this.peer.call(this.connectionState.opponentId, stream);
        this.mediaConnection = call;

        this.setupMediaConnectionListeners(call);

        return call;
    }

    /**
     * End the current media call.
     */
    endCall(): void {
        if (this.mediaConnection) {
            this.mediaConnection.close();
        }
        this.cleanupMedia();
        this.onCallEnded?.(); // Notify any listeners that call has ended
    }
    
    /**
     * Get the native RTCPeerConnection instance for advanced stream management.
     * Required for device changes (replaceTrack) and remote track listening (ontrack).
     * NOTE: This returns the RTCPeerConnection associated with the *latest* data or media connection.
     */
    getNativeConnection(): RTCPeerConnection | null {
        // PeerJS exposes the native RTCPeerConnection via the .peerConnection property on the connection object.
        if (this.mediaConnection && this.mediaConnection.peerConnection) {
            return this.mediaConnection.peerConnection;
        }
        if (this.connection && this.connection.peerConnection) {
            // Fallback to data connection's peer connection if no media connection
            return this.connection.peerConnection;
        }
        return null;
    }


    // ============================================
    // Private Methods - Setup
    // ============================================
    
    /**
     * Setup listeners for host peer
     */
    private setupHostListeners(): void {
        if (!this.peer) return;
        
        this.peer.on('connection', (conn: DataConnection) => {
            // Only accept one connection
            if (this.connection) {
                conn.close();
                return;
            }
            
            this.connection = conn;
            this.setupConnectionListeners(conn, () => {
                this.updateState({
                    status: ConnectionStatus.CONNECTED,
                    opponentId: conn.peer,
                });
                this.startHeartbeat();
                this.onConnected?.(conn.peer);
            });
        });
        
        this.peer.on('disconnected', () => {
            this.handlePeerDisconnected();
        });
    }

    /**
     * Setup the listener for incoming media calls from PeerJS
     * THIS WAS THE MISSING KEY PART!
     */
    private setupMediaCallListener(): void {
        if (!this.peer) return;

        this.peer.on('call', (call: MediaConnection) => {
            console.log('PeerConnection: Incoming media call received from:', call.peer);
            
            if (this.mediaConnection) {
                console.warn('Rejecting incoming call: already in a call.');
                call.close();
                return;
            }

            this.mediaConnection = call;
            this.setupMediaConnectionListeners(call);
            
            // Pass the call object to the VideoCall component to handle answering
            this.onIncomingCall?.(call);
        });
        
        this.peer.on('error', (err: Error) => {
            if (err.message.includes('No peer with that ID')) {
                console.warn('Call failed: Opponent not found.');
                this.onCallEnded?.();
            }
        });
    }
    
    /**
     * Setup listeners for a data connection
     */
    private setupConnectionListeners(
        conn: DataConnection,
        onOpen: () => void
    ): void {
        conn.on('open', () => {
            onOpen();
        });
        
        conn.on('data', (data: unknown) => {
            this.handleIncomingData(data);
        });
        
        conn.on('close', () => {
            this.handleConnectionClosed();
        });
        
        conn.on('error', (err: Error) => {
            this.handleError(err);
        });
    }

    /**
     * Setup listeners for a MediaConnection
     */
    private setupMediaConnectionListeners(call: MediaConnection): void {
        // 1. Remote Stream Event
        // The VideoCall component will primarily rely on the native `ontrack` event 
        // via `getNativeConnection()`, but this listener is essential for PeerJS's flow.
        call.on('stream', (remoteStream: MediaStream) => {
            console.log('PeerConnection: Received remote stream via PeerJS event.');
            // Note: The VideoCall component will handle setting srcObject in its own listener
        });

        // 2. Close Event
        call.on('close', () => {
            console.log('Media call closed.');
            this.cleanupMedia();
            this.onCallEnded?.();
        });
        
        // 3. Error Event
        call.on('error', (err: Error) => {
            console.error('Media connection error:', err);
            this.handleError(err);
            this.cleanupMedia();
            this.onCallEnded?.(); // Ensure the VideoCall component is notified to clean up UI
        });
    }
        
    /**
     * Cleanup media connection state
     */
    private cleanupMedia(): void {
        if (this.mediaConnection) {
            this.mediaConnection.removeAllListeners();
            this.mediaConnection = null;
        }
        // Important: Do NOT stop the track on the local stream here, 
        // the VideoCall component handles that in its `endCall` method.
    }
    
    // ============================================
    // Private Methods - Message Handling
    // ... (rest of the file remains the same)
    // ============================================

    /**
     * Handle incoming data from peer
     */
    private handleIncomingData(data: unknown): void {
        if (typeof data !== 'string') {
            console.warn('Received non-string data:', data);
            return;
        }
        
        const message = deserializeMessage(data);
        if (!message) {
            console.warn('Failed to deserialize message:', data);
            return;
        }
        
        // Handle internal messages
        if (message.type === MessageType.PING) {
            this.handlePing(message);
            return;
        }
        
        if (message.type === MessageType.PONG) {
            this.handlePong(message);
            return;
        }
        
        // Forward other messages to callback
        this.onMessage?.(message);
    }
    
    /**
     * Handle incoming ping message
     */
    private handlePing(message: GameMessage): void {
        if (message.type !== MessageType.PING) return;
        
        const pong = createPongMessage(
            this.peerId,
            message.sequence,
            message.timestamp
        );
        this.send(pong);
        
        this.lastHeartbeat = Date.now();
        this.updateState({ lastHeartbeat: this.lastHeartbeat });
    }
    
    /**
     * Handle incoming pong message
     */
    private handlePong(message: GameMessage): void {
        if (message.type !== MessageType.PONG) return;
        
        const sentTime = this.pendingPings.get(message.sequence);
        if (sentTime) {
            this.latency = Date.now() - sentTime;
            this.pendingPings.delete(message.sequence);
            this.updateState({ latency: this.latency });
            this.onLatencyUpdate?.(this.latency);
        }
        
        this.lastHeartbeat = Date.now();
        this.updateState({ lastHeartbeat: this.lastHeartbeat });
    }
    
    // ============================================
    // Private Methods - Heartbeat
    // ============================================
    
    /**
     * Start the heartbeat mechanism
     */
    private startHeartbeat(): void {
        this.stopHeartbeat();
        
        this.lastHeartbeat = Date.now();
        this.heartbeatInterval = setInterval(() => {
            this.sendPing();
            this.checkHeartbeatTimeout();
        }, HEARTBEAT_INTERVAL);
    }
    
    /**
     * Stop the heartbeat mechanism
     */
    private stopHeartbeat(): void {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
        this.pendingPings.clear();
    }
    
    /**
     * Send a ping message
     */
    private sendPing(): void {
        if (!this.isConnected()) return;
        
        const sequence = ++this.pingSequence;
        const ping = createPingMessage(this.peerId, sequence);
        
        this.pendingPings.set(sequence, Date.now());
        this.send(ping);
        
        // Clean up old pending pings
        const now = Date.now();
        for (const [seq, time] of this.pendingPings) {
            if (now - time > HEARTBEAT_TIMEOUT) {
                this.pendingPings.delete(seq);
            }
        }
    }
    
    /**
     * Check if heartbeat has timed out
     */
    private checkHeartbeatTimeout(): void {
        const timeSinceLastHeartbeat = Date.now() - this.lastHeartbeat;
        
        if (timeSinceLastHeartbeat > HEARTBEAT_TIMEOUT) {
            console.warn('Heartbeat timeout, connection may be lost');
            this.handleConnectionClosed();
        }
    }
    
    // ============================================
    // Private Methods - Reconnection
    // ============================================
    
    /**
     * Handle peer disconnection from signaling server
     * This is called when the peer loses connection to the PeerJS signaling server,
     * NOT when the data connection to the opponent closes.
     */
    private handlePeerDisconnected(): void {
        // Only attempt to reconnect to signaling server if we're actually disconnected
        // and reconnection is enabled
        if (this.shouldReconnect && this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            this.updateState({ status: ConnectionStatus.RECONNECTING });
            this.attemptSignalingReconnect();
        }
    }
    
    /**
     * Handle data connection closed
     * This is called when the WebRTC data connection to the opponent closes.
     */
    private handleConnectionClosed(): void {
        this.stopHeartbeat();
        this.connection = null;
        
        // For data connection loss, we need different handling than signaling server disconnection
        if (this.shouldReconnect && this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
            this.updateState({ status: ConnectionStatus.RECONNECTING });
            this.attemptDataReconnect();
        } else {
            this.updateState({
                status: ConnectionStatus.DISCONNECTED,
                opponentId: null,
            });
            this.onDisconnected?.('Connection closed');
        }
    }
    
    /**
     * Attempt to reconnect to the signaling server with exponential backoff
     * This is only for when we lose connection to the PeerJS server itself.
     */
    private attemptSignalingReconnect(): void {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        
        // Check if peer is actually disconnected from signaling server
        if (this.peer && !this.peer.disconnected) {
            console.log('Peer is still connected to signaling server, skipping reconnect');
            this.reconnectAttempts = 0;
            return;
        }
        
        const delay = Math.min(
            RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempts),
            RECONNECT_MAX_DELAY
        );
        
        this.reconnectAttempts++;
        console.log(`Attempting signaling reconnect ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);
        
        this.reconnectTimeout = setTimeout(() => {
            try {
                // Only call reconnect() if peer is actually disconnected from signaling server
                if (this.peer && this.peer.disconnected) {
                    this.peer.reconnect();
                } else {
                    console.log('Peer not disconnected, skipping reconnect call');
                    this.reconnectAttempts = 0;
                }
            } catch (error) {
                console.error('Signaling reconnect attempt failed:', error);
                if (this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
                    this.attemptSignalingReconnect();
                } else {
                    this.updateState({ status: ConnectionStatus.DISCONNECTED });
                    this.onDisconnected?.('Max reconnection attempts reached');
                }
            }
        }, delay);
    }
    
    /**
     * Attempt to reconnect the data connection with exponential backoff
     * This is for when the WebRTC data connection to the opponent is lost.
     */
    private attemptDataReconnect(): void {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
        }
        
        const delay = Math.min(
            RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempts),
            RECONNECT_MAX_DELAY
        );
        
        this.reconnectAttempts++;
        console.log(`Attempting data reconnect ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);
        
        this.reconnectTimeout = setTimeout(() => {
            try {
                if (this.isHost) {
                    // Host should NOT call peer.reconnect() - they wait for incoming connections
                    // The host's peer is still connected to signaling server, just the data connection was lost
                    // We just wait for the guest to reconnect to us
                    console.log('Host waiting for guest to reconnect...');
                    // Reset attempts since we're just waiting
                    this.reconnectAttempts = 0;
                    // Keep the reconnecting state but don't actively try to reconnect
                    // The guest will initiate a new connection
                } else {
                    // Guest needs to create a new data connection to host
                    if (this.peer && this.lastKnownPeerId && !this.peer.disconnected) {
                        console.log('Guest attempting to reconnect to host:', this.lastKnownPeerId);
                        this.connection = this.peer.connect(this.lastKnownPeerId, {
                            reliable: true,
                        });
                        
                        this.setupConnectionListeners(this.connection, () => {
                            this.reconnectAttempts = 0;
                            this.updateState({
                                status: ConnectionStatus.CONNECTED,
                                opponentId: this.lastKnownPeerId,
                            });
                            this.startHeartbeat();
                            this.onConnected?.(this.lastKnownPeerId);
                        });
                    } else if (this.peer?.disconnected) {
                        // If peer is disconnected from signaling server, reconnect to it first
                        console.log('Guest peer disconnected from signaling, reconnecting...');
                        this.attemptSignalingReconnect();
                    }
                }
            } catch (error) {
                console.error('Data reconnect attempt failed:', error);
                if (this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
                    this.attemptDataReconnect();
                } else {
                    this.updateState({ status: ConnectionStatus.DISCONNECTED });
                    this.onDisconnected?.('Max reconnection attempts reached');
                }
            }
        }, delay);
    }
    
    /**
     * Legacy method for backward compatibility
     * @deprecated Use attemptDataReconnect or attemptSignalingReconnect instead
     */
    private attemptReconnect(): void {
        // Determine which type of reconnection is needed
        if (this.peer?.disconnected) {
            this.attemptSignalingReconnect();
        } else {
            this.attemptDataReconnect();
        }
    }
    
    /**
     * Enable automatic reconnection
     */
    enableReconnect(): void {
        this.shouldReconnect = true;
        this.reconnectAttempts = 0;
    }
    
    /**
     * Disable automatic reconnection
     */
    disableReconnect(): void {
        this.shouldReconnect = false;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
    }
    
    // ============================================
    // Private Methods - Error Handling
    // ============================================
    
    /**
     * Handle errors
     */
    private handleError(error: Error): void {
        console.error('PeerConnection error:', error);
        
        this.updateState({ status: ConnectionStatus.ERROR });
        this.onError?.(error);
        
        // Attempt reconnect on certain errors
        if (this.shouldReconnect && error.message.includes('Lost connection')) {
            this.handleConnectionClosed();
        }
    }
    
    // ============================================
    // Private Methods - State Management
    // ============================================
    
    /**
     * Update connection state and notify listeners
     */
    private updateState(updates: Partial<ConnectionState>): void {
        this.connectionState = {
            ...this.connectionState,
            ...updates,
        };
        this.onStateChange?.(this.getConnectionState());
    }
    
    /**
     * Clean up all resources
     */
    private cleanup(): void {
        this.stopHeartbeat();
        this.cleanupMedia();
        
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        
        if (this.connection) {
            this.connection.close();
            this.connection = null;
        }
        
        if (this.peer) {
            this.peer.destroy();
            this.peer = null;
        }
        
        this.roomId = '';
        this.peerId = '';
        this.reconnectAttempts = 0;
    }
}